import useScreenBreakpoint from "@hooks/useScreenBreakpoint";
import moment from "moment";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getElasticSearchURLS } from "@selectors/enviromentSelectors";
import PersesSearchAPI from "@services/persesSearchService";
import {
  DASHBOARD_ACTIVITY_TYPES_DICTIONARY,
  DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
  QUERY_STATUS,
} from "@utils/constants/dashboard/dashboardModuleConstants";
import { RANGE_VALUE, getHistogramAxisFromDateRange } from "@utils/helpers/dateHelper";
import { getDateInterval } from "@utils/helpers/dashboardHelpers";

import useCalendarActivityTypes from "../../../hooks/useCalendarActivityTypes";
import { getFromAndToDate } from "./helpers";
import { LawsuitResponsible } from "@customTypes/user/responsible";
import { ChartSeries } from "@customTypes/chart";
import { CalendarActivityTypes } from "@customTypes/filter";
import { DoneActivitiesByResponsibleResponse } from "@customTypes/services/responses";

type ResultType = {
  xAxis: string[];
  groups?: {
    title: string;
    cols: number;
  }[];
  series: ChartSeries;
  noData?: boolean;
};

const parseDoneActivitiesByResponsibleLineChart = (
  response: DoneActivitiesByResponsibleResponse = [],
  dateRange: RANGE_VALUE,
  responsibleFilter: string[],
  responsibleArray: LawsuitResponsible[],
  isMobileScreen: boolean
) => {
  const noData = response.length === 0;
  const xAxis = getHistogramAxisFromDateRange(dateRange, isMobileScreen);

  const result: ResultType = {
    xAxis,
    groups: [],
    series: [],
    noData,
  };

  const years = xAxis.map((date) => moment(date).format("YYYY"));
  const uniqueYears = Array.from(new Set(years));

  uniqueYears.forEach((year) => {
    const cols = years.filter((y) => y === year).length;
    result.groups.push({ title: year, cols });
  });

  responsibleFilter.forEach((selectedResponsible) => {
    const responsibleNickname = responsibleArray.find(
      (responsible) => +responsible.id === +selectedResponsible
    )?.nickname;
    const series: ChartSeries[number] = {
      name: responsibleNickname,
      data: [],
    };
    xAxis.forEach((date) => {
      const dateCount = response
        .filter((bucket) => {
          const bool = bucket.firstKey === date && +bucket.secondKey === +selectedResponsible;
          return bool;
        })
        .reduce((sum, curr) => sum + curr.count, 0);
      series.data.push(dateCount || 0);
    });
    result.series.push(series);
  });

  return result;
};

type UseDoneActivitiesByResponsibleLineChartQuery = {
  (
    rangeFilter: DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
    activityType: CalendarActivityTypes,
    responsibleFilter: string[],
    responsibleArray: LawsuitResponsible[]
  ): [string, ResultType];
};

const useDoneActivitiesByResponsibleLineChartQuery: UseDoneActivitiesByResponsibleLineChartQuery = (
  rangeFilter,
  activityType,
  responsibleFilter,
  responsibleArray
) => {
  const [doneActivitiesByResponsible, setDoneActivitiesByResponsible] = useState({
    series: [],
    xAxis: [],
  });
  const [status, setStatus] = useState(QUERY_STATUS.loading);
  const { isMobileScreen } = useScreenBreakpoint();

  const { fromDate, toDate } = getFromAndToDate(rangeFilter);
  const dateInterval = getDateInterval(rangeFilter, isMobileScreen);
  const DASHBOARD_MODULE_ACTIVITY_TYPES = useCalendarActivityTypes();
  const activityFilter =
    activityType === DASHBOARD_ACTIVITY_TYPES_DICTIONARY.ALL.value
      ? DASHBOARD_MODULE_ACTIVITY_TYPES
      : [activityType];

  const PERSES_CODED_URLS = useSelector(getElasticSearchURLS);
  const perses = new PersesSearchAPI(PERSES_CODED_URLS);

  const { data, isSuccess, isLoading, isFetching } = useQuery(
    [
      "doneActivitiesByResponsible",
      { activityFilter, responsibleFilter, fromDate, toDate, dateInterval },
    ],
    () =>
      perses
        .getDoneActivitiesByResponsibleHistogram(
          activityFilter,
          responsibleFilter,
          fromDate,
          toDate,
          dateInterval
        )
        .then(({ buckets }) => buckets),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (isLoading) return;
    if (isFetching) {
      setStatus(QUERY_STATUS.refeching);
      return;
    }
    if (isSuccess) {
      const parsedData = parseDoneActivitiesByResponsibleLineChart(
        data,
        rangeFilter,
        responsibleFilter,
        responsibleArray,
        isMobileScreen
      );
      setDoneActivitiesByResponsible(parsedData);
      setStatus(QUERY_STATUS.success);
    }
  }, [
    data,
    isSuccess,
    responsibleFilter,
    responsibleArray,
    rangeFilter,
    isLoading,
    isFetching,
    isMobileScreen,
  ]);

  return [status, doneActivitiesByResponsible];
};

export default useDoneActivitiesByResponsibleLineChartQuery;
